import React, { useState } from 'react';
import { isDesktop, isTablet } from 'react-device-detect';
import styled, { css, keyframes } from 'styled-components';

import { DownIcon, PinIcon, UpIcon, Question, Recommend } from '../QPlayerSVGElement';

const fadeIn = keyframes`
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
  }

  to {
    opacity: 0;
  }
`;

interface ChatContainerProps {
    showChatWindow?: boolean;
    isMobile?: boolean;
    insidePresenter?: boolean;
	isPortedMobile?: boolean;
}

// Stacks the Poll and Q&A panels between the chat tab and the messages so they
// neither overlap each other nor cover the messages
export const EngagementPanelsStack = styled.div`
	qplayer-embed .qplayer-app & {
		flex-shrink: 0;
		max-height: 50%;
		overflow-y: auto;
		overflow-x: hidden;

		.poll__wrapper,
		.qna__wrapper {
			position: relative;
			top: 0;
		}
	}
`;

export const ChatContainer = styled.div<ChatContainerProps>`
	qplayer-embed .qplayer-app &.chat-container {
		position: absolute;
		right: 0;
		top: 0;
		font-size: 16px;

		visibility: ${(props) => (!props.showChatWindow ? 'hidden' : 'visible')};
		animation: ${(props) => (!props.showChatWindow ? fadeOut : fadeIn)} 1s linear;
		transition: visibility 1s linear;

		z-index: 30000;
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		overflow: hidden;
		background-color: ${({ isMobile }) => (isMobile ? 'transparent' : '#fff')};

		${({ insidePresenter }) =>
			insidePresenter &&
			css`
				box-shadow: 0 2px 6px 0 rgba(0, 0, 0, 0.12), 0 1px 2px 0 rgba(0, 0, 0, 0.08);
				animation: none;
				transition: width 0.5s, opacity 0.5s;
				margin-right: 16px;
			`}
		${({ isMobile }) =>
			isMobile &&
			css`
				width: ${(props: ChatContainerProps) => (props.showChatWindow ? '100%' : '0%')};
				height: calc(100% - 100px);
			`}
		${({ isMobile }) =>
			!isMobile &&
			css`
				width: ${(props: ChatContainerProps) => (props.showChatWindow ? '25%' : '0%')};
				height: 100%;
			`}
		${({ isPortedMobile }) =>
			isPortedMobile &&
			css`
				width: ${(props: ChatContainerProps) => (props.showChatWindow ? '100%' : '0%')};
				height: 80vh;
				margin: 0;
				transition: width 0.5s, opacity 0.5s;
			`}
	}
`;

export const ChatTabWrapper = styled.div`
	qplayer-embed .qplayer-app & {
		min-height: 45px;
		max-height: 45px;
		width: 100%;
		border-bottom: 1px solid #e5e9ec;
		display: flex;
		flex-direction: row;
		justify-content: space-evenly;
		align-items: center;
		position: relative;
	}
`;

export const ChatTabAction = styled.div`
	qplayer-embed .qplayer-app & {
		position: absolute;
		right: 0;
		top: 2px;
		bottom: 2px;
		display: flex;
		align-items: center;
		justify-content: center;
		svg {
			height: 18px;
			width: 18px;
		}
	}
`;

export const ChatTab = styled.div`
	qplayer-embed .qplayer-app & {
		text-align: center;
		letter-spacing: 0px;
		color: #000000;
		opacity: 1;
		font-weight: 700;
		display: flex;
		align-items: center;
		justify-content: center;
		height: 100%;
		font-size: 0.8125em;
	}
`;

export const StartOfChat = styled.div`
	qplayer-embed .qplayer-app & {
		text-align: center;
		letter-spacing: 0px;
		color: #000000;
		opacity: 0.6;
		padding: 4.5px;
		font-size: 0.75em;
	}
`;

export const ChatNotice = styled.div`
	qplayer-embed .qplayer-app & {
		letter-spacing: 0px;
		color: #000000;
		opacity: 0.6;
		padding: 0 47px 0 5px;
		font-size: 0.75em;
		text-align: right;
	}
`;

export const MessagesContainer = styled.div`
	qplayer-embed .qplayer-app & {
		padding-left: 12px;
		padding-right: 12px;
		height: 100%;
		flex: 1 1 0;
		min-height: 0;
		overflow: auto;
		position: relative;
		display: flex;
		flex-direction: column;

		&:empty {
			padding: 0 !important;
		}
	}
`;

interface MessengerTitleProps {
    isQuestion?: boolean;
    primaryColor?: string;
    isMobile?: boolean;
    isPinned?: boolean;
}

const MessengerTitle = styled.div<MessengerTitleProps>`
	qplayer-embed .qplayer-app & {
		text-align: left;
		font-weight: bold !important;
		font-size: 0.75em;
		font-family: inherit;
		letter-spacing: 0px;
		opacity: 1;
		display: flex;
		align-items: center;
		align-content: center;
		justify-content: space-between;
		pointer-events: all;

		span {
			display: inline-flex;
			align-items: center;
			color: inherit;
			font-weight: bold !important;
			svg {
				margin-right: 5px;
			}
		}
	}
`;

interface MessageContentProps {
    isQuestion?: boolean;
    isMobile?: boolean;
    isExpanded?: boolean;
    isPinned?: boolean;
}

const MessageContent = styled.div<MessageContentProps>`
	qplayer-embed .qplayer-app & {
		text-align: left;
		font-weight: normal;
		font-size: 0.75em;
		font-family: inherit;
		letter-spacing: 0px;
		color: ${({ isQuestion, isMobile }) => (isMobile && !isQuestion ? 'white' : '#000000')};
		overflow: hidden;
		overflow-wrap: anywhere;
		margin: 5px 0px;
	}
`;

interface MessageContainerProps {
    isMobile?: boolean;
    isContainerExpanded?: boolean;
    content?: string;
    isPinned?: boolean;
    primaryColor?: string;
    isAdmin?: boolean;
    isQuestion?: boolean;
}

const MessageContainer = styled.div<MessageContainerProps>`
	qplayer-embed .qplayer-app & {
		overflow-wrap: anywhere;
		width: fit-content;
		border-radius: ${({ isMobile }) => (isMobile ? '0' : '10px')};
		border-top-right-radius: 10px;
		border-bottom-right-radius: 10px;
		border-top-left-radius: 0px;
		opacity: ${({ isContainerExpanded, isMobile }) => (isMobile ? (isContainerExpanded ? 1 : 'inherit') : '1')};
		position: relative;

		${(props) =>
			props.content
				? css`
						padding: 7px 12px 10px 12px;
						margin-bottom: ${({ isMobile }: MessageContainerProps) => (isMobile ? '1px' : '8px')};
					`
				: css``};

		${({ isPinned, content, primaryColor, isMobile }) =>
			isPinned
				? css`
						&.chat-message--pinned {
							visibility: visible;
							background: ${() => (isMobile ? primaryColor : '#fff')};
							padding-top: ${() => (content ? '10px' : '')};
							width: auto;
							opacity: 1;
							border-top-right-radius: 4px;
							border-bottom-right-radius: 4px;
							border-radius: ${() => (isMobile ? '0' : '4px')};
							border: 1px solid rgba(0, 0, 0, 0.05);
							&:empty {
								visibility: hidden;
							}

							${MessengerTitle} {
								color: ${primaryColor};
								svg {
									fill: ${isMobile ? '#ffffff' : '#000000'};
								}
							}

							${MessageContent} {
								color: ${isMobile ? '#ffffff' : '#000000'};
							}
						}
					`
				: css`
						background: ${() => (isMobile ? '#000' : '#f2f5f7')};
					`};

		${({ isAdmin, isPinned, primaryColor }) =>
			isAdmin &&
			!isPinned &&
			css` &.chat-message--admin {
				background: ${primaryColor};
				padding: 7px 16px 10px 16px;
				margin-bottom: 4px;
				color: #ffffff;

				${MessengerTitle} {
					color: #ffffff;
				}

				${MessageContent} {
					color: #ffffff;
				}
			}`}

		${({ isQuestion, primaryColor }) =>
			isQuestion &&
			css` &.chat-message--question {
				border: 1px solid;
				border-color: ${() => primaryColor ?? '#326AF3'};
				background: #ffffff;
				border-top-left-radius: 10px;

				${MessengerTitle} {
					color: ${() => primaryColor ?? '#326AF3'};
				}
			}`}
	}
`;

interface MessageQuoteProps {
    isPinned?: boolean;
    isMobile?: boolean;
}

const MessageQuote = styled.div<MessageQuoteProps>`
	qplayer-embed .qplayer-app & {
		text-align: left;
		font: normal normal 300 11px/14px;
		font-family: inherit;
		letter-spacing: 0px;
		color: #ffffff;
		opacity: 0.77;

		border-left: 2px solid;
		padding-left: 8px;
		margin-top: 5px;
		margin-bottom: 5px;

		${(props) =>
			props.isPinned &&
			css`
				color: ${() => (props.isMobile ? 'white' : '#000000')};
			`}
	}
`;

interface MessageInputContainerProps {
    isMobile?: boolean;
}

export const MessageInputContainer = styled.div<MessageInputContainerProps>`
	qplayer-embed .qplayer-app & {
		${({ isMobile }) =>
			isMobile &&
			css`
				background: #000;
			`}

		${() =>
			isDesktop &&
			!isTablet &&
			css`
				background: transparent;
			`}

		padding: 5px;
		position: relative;
		display: flex;
		justify-content: space-between;
		align-items: center;
	}
`;

interface MessageInputProps {
    isMobile?: boolean;
}

export const MessageInput = styled.input<MessageInputProps>`
	qplayer-embed .qplayer-app & {
		text-align: left;
		font-weight: normal;
		font-size: 0.75em;
		font-family: inherit;
		width: calc(100% - 40px);
		box-sizing: border-box;
		padding: 10px;
		border: 1px solid #000;
		border-radius: 4px;
		overflow: auto;
		background-color: #ffffff;
		padding-right: 40px;

		:empty:before {
			content: attr(placeholder);
			cursor: text;
		}

		:disabled {
			color: rgba(118, 118, 118, 0.3);
			border-color: rgba(118, 118, 118, 0.3);
		}

		${({ isMobile }) =>
			isMobile &&
			css`
				color: #fff;
			`}

		${({ isMobile }) =>
			!isMobile &&
			css`
				color: #000000;
			`}
	}
`;

export const MessageNotification = styled.div`
	qplayer-embed .qplayer-app & {
		position: absolute;
		background: transparent;
		height: 32px;
		display: flex;
		align-items: center;
		justify-content: center;
		left: 0px;
		right: 0px;
		padding-bottom: 3px;
		top: -32px;
	}
`;

export const MessageNotificationContent = styled.div`
	qplayer-embed .qplayer-app & {
		background-color: white;
		top: 0px;
		bottom: 0px;
		border-radius: 16px;
		padding: 0px 10px;
		font-size: 0.75em;
		display: flex;
		justify-content: center;
		align-items: center;
		height: 100%;
		cursor: pointer;
		color: #000;
		border: 1px solid #f2f5f7;
		svg {
			margin-left: 5px;
		}
		&:hover {
			font-weight: 600;
		}
	}
`;

export const RecommendButtonContainer = styled.div`
	qplayer-embed .qplayer-app & {
	}
`;

interface RecommendButtonProps {
    primaryColor?: string;
}
export const RecommendButton = styled.button<RecommendButtonProps>`
	qplayer-embed .qplayer-app & {
		border: 1px solid #aaafb2;
		background: #f2f5f7 0% 0% no-repeat padding-box;
		border-radius: 100px;
		display: flex;
		align-items: center;
		height: 32px;
		width: 60px;
		justify-content: center;
		padding: 0px;

		&:not(:disabled):hover {
			border: 1px solid ${props => props.primaryColor ?? '#000'};
			cursor: pointer;
		}

		span {
			margin-left: 5px;
			color: #000000;
		}
	}
`;

export const EmojiIcon: React.FC = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18">
        <path
            id="Path_9854"
            data-name="Path 9854"
            d="M33.248,24.606a8.642,8.642,0,1,0,8.642,8.642A8.652,8.652,0,0,0,33.248,24.606Zm-2.593,5.617a2.291,2.291,0,0,1,1.539.622.648.648,0,0,1-.911.922.92.92,0,0,0-1.257,0,.653.653,0,0,1-.913-.017.643.643,0,0,1,0-.906A2.293,2.293,0,0,1,30.655,30.223Zm5.185,0a2.291,2.291,0,0,1,1.538.622.648.648,0,1,1-.917.916.92.92,0,0,0-1.251.006.653.653,0,0,1-.913-.017.643.643,0,0,1,0-.906A2.293,2.293,0,0,1,35.84,30.223Zm-6.2,4.322a.649.649,0,0,1,.557.246,3.884,3.884,0,0,0,6.1,0,.648.648,0,0,1,1.019.8,5.181,5.181,0,0,1-8.141,0,.649.649,0,0,1,.109-.911A.64.64,0,0,1,29.639,34.545Z"
            transform="translate(-24.606 -24.606)"
        />
    </svg>
);

interface EmojiIconContainerProps {
    closeIncomingMessage?: boolean;
}

export const EmojiIconContainer = styled.div<EmojiIconContainerProps>`
	qplayer-embed .qplayer-app & {
		position: absolute;
		right: 45px;
		top: 5px;
		width: 40px;
		height: 40px;
		display: flex;
		justify-content: center;
		align-items: center;
		border-radius: 50%;
		${(props) =>
			props.closeIncomingMessage &&
			css`
				color: rgba(0, 0, 0, 0.4);
			`}
		${(props) =>
			!props.closeIncomingMessage &&
			css`
				cursor: pointer;
				&:hover {
					background-color: rgba(0, 0, 0, 0.04);
				}
			`}
	}
`;

const BluePinIcon = styled(PinIcon)`
	qplayer-embed .qplayer-app & {
		fill-rule: nonzero;
		clip-rule: evenodd;
		stroke-linecap: round;
		stroke-linejoin: round;
		margin-right: 5px;
		color: #000000;
		path {
			fill: #000000;
		}
	}
`;

export interface MessageProps {
    isAdmin: boolean;
    name: string;
    content?: string;
    replyContent?: string;
    isPinned?: boolean;
    id: string;
    votes: number;
    messageContainerRef?: React.RefObject<HTMLDivElement>;
    isContainerExpanded?: boolean;
    notificationComponent?: () => React.ReactNode;
    primaryColor: string;
    isQuestion?: boolean;
    onUpvoteButtonClick?: (_e: React.MouseEvent<HTMLButtonElement>, _id: string) => void;
    isVoted?: boolean;
	isVoteable?: boolean;
}

export const Message: React.FC<MessageProps> = ({
    isAdmin,
    name,
    content,
    replyContent,
    isPinned,
    id,
    votes,
    messageContainerRef,
    isContainerExpanded,
    notificationComponent,
    primaryColor,
    isQuestion,
    onUpvoteButtonClick,
    isVoted,
	isVoteable
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const showExpansion = content?.length && content.length > 110 || replyContent;

    return (
        <MessageContainer
            isContainerExpanded={isContainerExpanded}
            isAdmin={isAdmin}
            isPinned={!!isPinned}
            id={id}
            ref={messageContainerRef}
            className={['chat-message',
				isPinned ? 'chat-message--pinned' : '',
				!replyContent && isQuestion ? 'chat-message--question' : '',
				isAdmin ? 'chat-message--admin' : ''
			]
                .join(' ')
                .trim()}
            content={content}
            primaryColor={primaryColor}
			isQuestion={!!(!replyContent && isQuestion)}
        >
            {notificationComponent?.()}
            {content && (
                <>
                    <MessengerTitle
                        onClick={isPinned && showExpansion ? () => setIsExpanded((prevState) => !prevState) : undefined}
                        isPinned={isPinned}
                        isQuestion={(!replyContent && isQuestion)}
						style={{ color: (!replyContent && isQuestion) || isPinned ? primaryColor : isAdmin ? "#ffffff" : "#000000" }}
                    >
                        <span>
                            {isPinned && <BluePinIcon color={"#000000"}/>}
                            {!replyContent && isQuestion && <Question color={primaryColor} />}
                            {name}
                        </span>
                        {isPinned && showExpansion && <>{isExpanded ? <DownIcon /> : <UpIcon />}</>}
                    </MessengerTitle>

                    <MessageContent
						isExpanded={isExpanded}
						isPinned={isPinned}
						className={'chat-message__content'}
						isQuestion={!!(!replyContent && isQuestion)}
					>
                        {replyContent && <MessageQuote isPinned={isPinned}>{replyContent}</MessageQuote>}
                        {content}
                    </MessageContent>

                    {!replyContent && isQuestion && (
                        <RecommendButtonContainer>
                            <RecommendButton
                                onClick={(e) => {
									if (!isVoteable || isVoted) {
										return;
									}
									onUpvoteButtonClick?.(e, id);
								}}
                                disabled={isVoted || !isVoteable}
                                style={isVoted || !isVoteable ? { opacity: 0.8 } : undefined}
								primaryColor={primaryColor}
                            >
                                <Recommend color='#000' />
                                <span>{votes}</span>
                            </RecommendButton>
                        </RecommendButtonContainer>
                    )}
                </>
            )}
        </MessageContainer>
    );
};
